"use client"

import React from "react"

interface ComponentMetrics {
  name: string
  renderTime: number
  mountTime: number
  updateCount: number
}

class ClientPerformanceMonitor {
  private metrics: Map<string, ComponentMetrics> = new Map()
  private renderStartTimes: Map<string, number> = new Map()
  private enabled = process.env.NODE_ENV === "development"

  startRender(componentName: string) {
    if (!this.enabled) return
    this.renderStartTimes.set(componentName, performance.now())
  }

  endRender(componentName: string) {
    if (!this.enabled) return

    const startTime = this.renderStartTimes.get(componentName)
    if (!startTime) return

    const renderTime = performance.now() - startTime
    const existing = this.metrics.get(componentName)

    if (existing) {
      existing.renderTime = Math.max(existing.renderTime, renderTime)
      existing.updateCount++
    } else {
      this.metrics.set(componentName, {
        name: componentName,
        renderTime,
        mountTime: renderTime,
        updateCount: 1,
      })
    }

    // Log slow renders
    if (renderTime > 16) {
      console.warn(`🐌 Slow render: ${componentName} took ${renderTime.toFixed(2)}ms`)
    }

    this.renderStartTimes.delete(componentName)
  }

  logSlowComponents() {
    if (!this.enabled) return

    const slowComponents = Array.from(this.metrics.values())
      .filter((m) => m.renderTime > 10)
      .sort((a, b) => b.renderTime - a.renderTime)

    if (slowComponents.length > 0) {
      console.group("🐌 Slow Components")
      slowComponents.forEach((comp) => {
        console.log(`${comp.name}: ${comp.renderTime.toFixed(2)}ms (${comp.updateCount} renders)`)
      })
      console.groupEnd()
    }
  }

  measureBundleSize() {
    if (typeof window === "undefined") return

    // Measure main thread blocking
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > 50) {
          console.warn(`🚫 Long task: ${entry.duration.toFixed(2)}ms`)
        }
      }
    })
    observer.observe({ entryTypes: ["longtask"] })

    // Measure script loading
    const scriptObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name.includes("chunks") && entry.duration > 100) {
          console.warn(`📦 Large script: ${entry.name} took ${entry.duration.toFixed(2)}ms`)
        }
      }
    })
    scriptObserver.observe({ entryTypes: ["navigation", "resource"] })
  }
}

export const clientPerfMonitor = new ClientPerformanceMonitor()

// React hook for component performance
export function useClientPerformance(componentName: string) {
  const startTime = performance.now()

  React.useEffect(() => {
    clientPerfMonitor.startRender(componentName)
    return () => {
      clientPerfMonitor.endRender(componentName)
    }
  })

  React.useEffect(() => {
    const renderTime = performance.now() - startTime
    if (renderTime > 16) {
      console.warn(`🐌 ${componentName} mount took ${renderTime.toFixed(2)}ms`)
    }
  }, [componentName, startTime])
}
